import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { dealApi, loanApplicationCommonInfoApi } from 'app/services/api';
import { SKIP_FORBIDDEN } from 'app/services/api/httpConfig';

import { PpeType } from 'constants/company';

import {
  selectBorrowersCommonInfoGroupsByRefNumber,
  selectIsSaveProgramPermissionsLack,
  selectNbesCommonInfoGroupsByRefNumber,
  selectPrimaryBorrowerIdCommonInfo,
} from 'store/loanApplications/selectors';

/**
 * The loan-action runner context for hosts outside the loan editor (e.g. the task pipeline
 * modal). Loads the application's common info and deal into LOCAL state keyed by applicationId —
 * never the global loan store — and derives the same values the loan-editor selectors give. Only
 * a successful load for the current application produces a context; a late response for another
 * application is dropped. Loan-editor-only state (pending changes, AUS) stays empty, and
 * `isLoanEditor: false` keeps the run flow from refreshing or remounting loan-editor tabs.
 * @param {{ applicationId: string|null, loanId: string|null, isEnabled: boolean }} params
 * @returns {{ runnerContext: object|null, isFailed: boolean }}
 */
export const useStandaloneRunnerContext = ({ applicationId, loanId, isEnabled }) => {
  const pathBeforeInitialRedirect = useRef(null);

  const [loaded, setLoaded] = useState({ applicationId: null, common: null, deal: null });
  const [failedApplicationId, setFailedApplicationId] = useState(null);

  const isSaveProgramPermissionsLack = useSelector(selectIsSaveProgramPermissionsLack);

  const isCurrent = !!applicationId && loaded.applicationId === applicationId;

  const runnerContext = useMemo(() => {
    if (!isCurrent) {
      return null;
    }

    const localState = { loanApplication: { common: loaded.common } };
    const pricingEngineType = loaded.common?.pricingEngineType ?? null;

    return {
      isLoanEditor: false,
      applicationId,
      loanId,
      subTab: null,
      pathBeforeInitialRedirect,
      searchParams: {},
      borrowerGroups: selectBorrowersCommonInfoGroupsByRefNumber(localState),
      nonBorrowerEntitiesGroups: selectNbesCommonInfoGroupsByRefNumber(localState),
      primaryBorrowerId: selectPrimaryBorrowerIdCommonInfo(localState),
      isSaveProgramPermissionsLack,
      isAusSubmitting: false,
      isAusSummaryExist: false,
      ausSummary: null,
      isPendingChangesLoaded: false,
      isPendingChanges: false,
      isOptimalBlueEngine: pricingEngineType === PpeType.OPTIMAL_BLUE_PPE,
      applicationStatus: loaded.deal?.applicationStatus ?? null,
      loanNumber: loaded.common?.loanNumber ?? null,
      pricingEngineType,
    };
  }, [applicationId, isCurrent, isSaveProgramPermissionsLack, loaded, loanId]);

  useEffect(() => {
    if (!isEnabled || !applicationId) {
      return;
    }

    const request = { isCancelled: false };

    Promise.all([
      loanApplicationCommonInfoApi.getApplicationCommonInfo({ applicationId }, SKIP_FORBIDDEN),
      dealApi.getDataByApplicationId({ applicationId }, SKIP_FORBIDDEN),
    ])
      .then(([{ data: common }, { data: deal }]) => {
        if (request.isCancelled) {
          return;
        }

        setLoaded({ applicationId, common: common ?? {}, deal: deal ?? {} });
      })
      .catch(() => {
        if (!request.isCancelled) {
          setFailedApplicationId(applicationId);
        }
      });

    return () => {
      request.isCancelled = true;
    };
  }, [applicationId, isEnabled]);

  return { runnerContext, isFailed: !!applicationId && failedApplicationId === applicationId };
};
